import { FC, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

import { Menu, Preloader, TrainCard, Trains } from "@/components";
import { PreloaderCat } from "@/components/Preloader/PreloaderCat";
import { ProgramInfoBlock } from "@/components/ProgramInfoBlock";
import { useCreateTrainingMutation } from "@/store/trainingsApi";
import { useSelectSampleMutation, useGetUserMutation, useLazyGetAdaptiveStatusQuery, useRefreshSamplesMutation, useQuizFindAnswerMutation } from "@/store/usersApi";
import { Training } from "@/components/Trains/model";
import { User } from "@/entities/user";
import { Slider } from "@/components/Main/Slider";
import weekCalendar from "@/assets/week-calendar.png";
import style from "./TrainsPage.module.scss";
import {
  AdaptiveAttachedMenu,
  UserAgreementLink,
} from "@/widgets";
import { CreateTrainingModal } from "@/features/create-training-modal";
import { useDeleteTrainingMutation } from "@/store/trainingsApi";
import { tg } from "@/constants/telegram";
import { isQuizNotCompletedError, notifyQuizNotCompleted } from "@/shared/utils";
import { useAppSelector } from "@/shared/hooks";

const NO_PROGRESS_MESSAGE = "У вас еще нет прогресса";
const GET_PROGRAM_BUTTON_TEXT = "Получить программу";
const TRAINING_SAMPLE_LINK = "https://t.me/FitGuid_bot?start=getTrainingSample";

type TrainsPageProps = {
  initData: string;
  user: User;
};

export enum UserStatus {
  REQUIRED_ANCET = "Required ancet",
  SELECTED = "Selected",
}

export const TrainsPage: FC<TrainsPageProps> = (props) => {
  const { initData, user } = props;
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [isReady, setIsReady] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(user || null);

  const [interpretation, setInterpretation] = useState<string>("");
  const [stages, setStages] = useState<string[]>([]);
  const [accent, setAccent] = useState<number[]>([]);
  const [injuries, setInjuries] = useState<string[]>([]);
  const [trainingDuration, setTrainingDuration] = useState<string>("");
  const [trainings, setTrainings] = useState<Training[] | null>(null);
  const [userTrainings, setUserTrainings] = useState<Training[]>([]);
  const [scheduleTrainings, setScheduleTrainings] = useState<Training[]>([]);
  const [trainingCount, setTrainingCount] = useState<number>(0);
  const [allowedTrainingIds, setAllowedTrainingIds] = useState<number[]>([]);
  const [isShowTemplates, setShowTemplates] = useState<boolean>(true);
  const [isAdaptivePolling, setIsAdaptivePolling] = useState(false);
  const [adaptiveTaskId, setAdaptiveTaskId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"user" | "ai">("ai");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedUserDate, setSelectedUserDate] = useState<Date | null>(null);
  const [weekOffset, setWeekOffset] = useState<number>(0); // Смещение недель для календаря
  const [userWeekOffset, setUserWeekOffset] = useState<number>(0); // Смещение недель для пользовательских тренировок
  const [calendarDirection, setCalendarDirection] = useState<"forward" | "backward">("forward"); // Направление анимации календаря
  const [userCalendarDirection, setUserCalendarDirection] = useState<"forward" | "backward">("forward"); // Направление анимации календаря для пользовательских тренировок
  const [showAdaptiveTextarea, setShowAdaptiveTextarea] = useState(false);
  const [adaptiveUserMsg, setAdaptiveUserMsg] = useState("");
  const adaptiveTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [showCreateTrainingModal, setShowCreateTrainingModal] = useState(false);
  const isFetchingTrainingsRef = useRef(false);

  const navigate = useNavigate();

  const POLLING_TIMEOUT = 180000; // 3 минуты в миллисекундах

  // Проверяем есть ли у пользователя активная подписка
  const hasActiveSubscription = Boolean(currentUser?.user?.subscription && (
    currentUser.user.subscription.end_date === null || // безлимитная подписка
    new Date(currentUser.user.subscription.end_date) > new Date() // подписка с датой окончания
  ));


  const [createTraining, { isLoading: isTrainLoading }] =
    useCreateTrainingMutation();
  const [createTemplate, { isLoading: isTemplateLoading }] =
    useSelectSampleMutation();
  const [getUser] = useGetUserMutation();
  const [getAdaptiveStatus] = useLazyGetAdaptiveStatusQuery();
  const [deleteTraining] = useDeleteTrainingMutation();
  const [refreshSamples] = useRefreshSamplesMutation();
  const [quizFindAnswer] = useQuizFindAnswerMutation();

  // Функция для получения ID области по названию
  const getAreaIdByName = (name: string): number | null => {
    if (!name || typeof name !== 'string') return null;
    
    const normalizedName = name.trim();
    const areaMap: Record<string, number> = {
      "Шея": 1,
      "Плечи / Плечевой пояс": 2,
      "Плечи": 2,
      "Плечевой пояс": 2,
      "Руки (Верхняя часть/Плечо, Предплечье)": 3,
      "Руки": 3,
      "Спина (Верхняя, Нижняя/Поясница)": 4,
      "Спина": 4,
      "Грудь": 5,
      "Живот (Пресс)": 6,
      "Живот": 6,
      "Пресс": 6,
      "Таз / Ягодицы": 7,
      "Таз": 7,
      "Ягодицы": 7,
      "Бедра (Передняя часть, Задняя часть, Внутренняя часть)": 8,
      "Бедра": 8,
      "Голени (Икры, Передняя часть)": 9,
      "Голени": 9,
      "Икры": 9,
    };
    
    // Прямое совпадение
    if (areaMap[normalizedName]) {
      return areaMap[normalizedName];
    }
    
    // Частичное совпадение
    for (const [key, value] of Object.entries(areaMap)) {
      if (normalizedName.includes(key) || key.includes(normalizedName)) {
        return value;
      }
    }
    
    return null;
  };

  const getTraings = useCallback(async () => {
    // Работаем и с initData, и с JWT авторизацией
    const hasAuth = initData || isAuthenticated || localStorage.getItem("access_token");
    if (!hasAuth) {
      return;
    }

    // Не запускаем параллельные одинаковые загрузки, чтобы избежать лишних перезапросов.
    if (isFetchingTrainingsRef.current) {
      return;
    }
    isFetchingTrainingsRef.current = true;

    try {
      // Обновляем данные пользователя для получения актуальной информации о подписке
      const userResult = await getUser().unwrap();
      if (userResult) {
        setCurrentUser(userResult);
      } else if (user) {
        setCurrentUser(user);
      }
      
      setShowTemplates(userResult?.select_samples !== "Selected");

      const hasSampleTrainings = Array.isArray(userResult?.select_samples)
        && userResult.select_samples.some(
          (sample) => Array.isArray(sample.trainings) && sample.trainings.length > 0
        );

      if (
        userResult?.select_samples === UserStatus.REQUIRED_ANCET
        && !hasSampleTrainings
      ) {
        navigate("/quiz", { replace: true });
        return;
      }

      if (Array.isArray(userResult?.select_samples)) {
        setTrainings(null);
        setIsReady(true);
        return;
      }

      const trainResult = await createTraining().unwrap();
      
      // Новая структура: trainings это объект с created_by_user и schedule
      const trainingsData = trainResult.trainings || {};
      const loadedUserTrainings = Array.isArray(trainingsData.created_by_user) 
        ? trainingsData.created_by_user 
        : [];
      const loadedScheduleTrainings = Array.isArray(trainingsData.schedule) 
        ? trainingsData.schedule 
        : [];
      
      // Для обратной совместимости объединяем все тренировки
      const allTrainings = [...loadedUserTrainings, ...loadedScheduleTrainings];
      
      const count = trainResult.training_count || 0;
      
      setTrainingCount(count);
      setUserTrainings(loadedUserTrainings);
      setScheduleTrainings(loadedScheduleTrainings);
      setTrainings(allTrainings);
      setInterpretation(trainResult.interpretation || "");
      setStages(Array.isArray(trainResult.stages) ? trainResult.stages : []);
      
      // Вычисляем доступные тренировки для пользователей без подписки
      const accessibleIds = (() => {
        if (!loadedScheduleTrainings || loadedScheduleTrainings.length === 0) {
          return [];
        }

        // Проверяем подписку из userResult
        const userHasSubscription = Boolean(userResult?.user?.subscription && (
          userResult.user.subscription.end_date === null ||
          new Date(userResult.user.subscription.end_date) > new Date()
        ));

        // Если есть подписка, все тренировки доступны
        if (userHasSubscription) {
          return loadedScheduleTrainings.map((trainItem: Training) => trainItem.id);
        }

        // Если нет подписки, доступны только первые training_count актуальных тренировок
        // НО должно быть открыто НЕ МЕНЕЕ 3 тренировок
        // Фильтруем только актуальные тренировки (status === 'wait' || null)
        const currentTrainings = loadedScheduleTrainings.filter(
          (train: Training) => train.status === 'wait' || train.status === null || train.status === undefined
        );

        // Сортируем актуальные тренировки по дате
        const sortedTrainings = [...currentTrainings].sort((a, b) => {
          const firstDate = new Date(a.schedule).getTime();
          const secondDate = new Date(b.schedule).getTime();
          return firstDate - secondDate;
        });

        // Используем максимум из training_count и 3 (минимум 3 тренировки)
        const allowedCount = Math.max(3, count);
        const allowedTrainings = sortedTrainings.slice(0, allowedCount);

        return allowedTrainings.map((trainItem: Training) => trainItem.id);
      })();

      setAllowedTrainingIds(accessibleIds);
      setIsReady(true);

      // Получаем данные из quiz
      try {
        const quizData = await quizFindAnswer({
          data_type: ['accent', 'injuries', 'training_duration']
        }).unwrap();

        if (quizData.answers) {
          // Обрабатываем accent (приходит как массив строк с названиями областей)
          const accentValue = quizData.answers['accent'];
          
          if (accentValue !== undefined && accentValue !== null) {
            try {
              let accentArray: number[] = [];
              
              // Если это строка, пытаемся распарсить как JSON или разделить по запятой
              if (typeof accentValue === 'string') {
                // Пытаемся распарсить как JSON массив
                try {
                  const parsed = JSON.parse(accentValue);
                  if (Array.isArray(parsed)) {
                    accentArray = parsed.map(v => {
                      // Если это строка с названием, ищем ID по названию
                      if (typeof v === 'string') {
                        const areaId = getAreaIdByName(v);
                        if (areaId) return areaId;
                        // Если не нашли по названию, пытаемся распарсить как число
                        const num = parseInt(v);
                        return !isNaN(num) && num > 0 ? num : null;
                      }
                      return typeof v === 'number' && v > 0 ? v : null;
                    }).filter((v): v is number => v !== null && !isNaN(v) && v > 0);
                  } else {
                    // Если не массив, пытаемся разделить по запятой
                    accentArray = accentValue.split(',').map(v => {
                      const trimmed = v.trim();
                      const areaId = getAreaIdByName(trimmed);
                      if (areaId) return areaId;
                      const num = parseInt(trimmed);
                      return !isNaN(num) && num > 0 ? num : null;
                    }).filter((v): v is number => v !== null && !isNaN(v) && v > 0);
                  }
                } catch {
                  // Если не JSON, разделяем по запятой
                  accentArray = accentValue.split(',').map(v => {
                    const trimmed = v.trim();
                    const areaId = getAreaIdByName(trimmed);
                    if (areaId) return areaId;
                    const num = parseInt(trimmed);
                    return !isNaN(num) && num > 0 ? num : null;
                  }).filter((v): v is number => v !== null && !isNaN(v) && v > 0);
                }
              } else if (Array.isArray(accentValue)) {
                // Это массив - обрабатываем каждый элемент
                accentArray = accentValue.map(v => {
                  if (typeof v === 'string') {
                    const areaId = getAreaIdByName(v);
                    if (areaId) return areaId;
                    const num = parseInt(v);
                    return !isNaN(num) && num > 0 ? num : null;
                  }
                  return typeof v === 'number' && v > 0 ? v : null;
                }).filter((v): v is number => v !== null && !isNaN(v) && v > 0);
              } else if (typeof accentValue === 'number') {
                accentArray = accentValue > 0 ? [accentValue] : [];
              }
              
              if (accentArray.length > 0) {
                setAccent(accentArray);
              }
            } catch (err) {
              // Ошибка парсинга accent - игнорируем
            }
          }

          // Обрабатываем injuries (может быть массивом строк или строкой)
          const injuriesValue = quizData.answers['injuries'];
          if (injuriesValue !== undefined && injuriesValue !== null) {
            try {
              if (typeof injuriesValue === 'string') {
                // Пытаемся распарсить как JSON массив
                try {
                  const parsed = JSON.parse(injuriesValue);
                  if (Array.isArray(parsed)) {
                    setInjuries(parsed.map(v => String(v)).filter(v => v && v !== 'null' && v !== 'undefined'));
                  } else {
                    setInjuries([String(injuriesValue)]);
                  }
                } catch {
                  setInjuries([String(injuriesValue)]);
                }
              } else if (Array.isArray(injuriesValue)) {
                setInjuries(injuriesValue.map(v => String(v)).filter(v => v && v !== 'null' && v !== 'undefined'));
              } else {
                setInjuries([String(injuriesValue)]);
              }
            } catch (err) {
              // Ошибка парсинга injuries - игнорируем
            }
          }

          // Обрабатываем training_duration
          const durationValue = quizData.answers['training_duration'];
          if (durationValue !== undefined && durationValue !== null) {
            setTrainingDuration(String(durationValue));
          }
        }
      } catch (err) {
        console.error("Error fetching quiz data:", err);
      }
    } catch (err) {
      console.error("Error init:", err);
      setTrainings([]);
      setShowTemplates(true);
      setIsReady(true);
    } finally {
      isFetchingTrainingsRef.current = false;
    }
  }, [initData, isAuthenticated, getUser, user, createTraining, quizFindAnswer, navigate]);


  useEffect(() => {
    const hasAuth = initData || isAuthenticated || localStorage.getItem("access_token");
    if (hasAuth) {
      getTraings();
    }
    
    // Проверяем есть ли активная адаптация в localStorage
    const savedTaskId = localStorage.getItem('adaptive_task_id');
    if (savedTaskId) {
      // Проверяем статус сохраненного task_id при загрузке
      getAdaptiveStatus({ task_id: savedTaskId })
        .unwrap()
        .then((statusResult) => {
          if (statusResult.status === 'success') {
            // Если задача успешно завершена, очищаем
            localStorage.removeItem('adaptive_task_id');
          } else if (statusResult.status === 'error') {
            // Если ошибка, очищаем и редиректим на /equipments
            localStorage.removeItem('adaptive_task_id');
            navigate('/equipments');
          } else if (statusResult.status === 'runing') {
            // Если задача еще выполняется, продолжаем поллинг
            setAdaptiveTaskId(savedTaskId);
            setIsAdaptivePolling(true);
          }
        })
        .catch(() => {
          // Если task_id не найден (404), очищаем и редиректим на /trains
          localStorage.removeItem('adaptive_task_id');
          navigate('/trains');
        });
    }
  }, [initData, isAuthenticated, getTraings, getAdaptiveStatus, navigate]);

  useEffect(() => {
    const hasAuth = initData || isAuthenticated || localStorage.getItem("access_token");
    if (!isAdaptivePolling || !adaptiveTaskId || !hasAuth) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const statusResult = await getAdaptiveStatus({ task_id: adaptiveTaskId }).unwrap();
        
        if (statusResult.status === 'success') {
          setIsAdaptivePolling(false);
          setAdaptiveTaskId(null);
          localStorage.removeItem('adaptive_task_id');
          clearInterval(pollInterval);
          // Обновляем тренировки после завершения адаптации
          getTraings();
          toast.success("Программа обновлена");
        } else if (statusResult.status === 'error') {
          setIsAdaptivePolling(false);
          setAdaptiveTaskId(null);
          localStorage.removeItem('adaptive_task_id');
          clearInterval(pollInterval);
          toast.error("Произошла ошибка при генерации программы");
          navigate('/equipments');
        }
        // Если статус 'runing', продолжаем поллинг
      } catch (err: any) {
        // Если task_id не найден (404), очищаем и редиректим на /trains
        if (err?.status === 404 || err?.statusCode === 404) {
          console.error("Task ID не найден, очищаем:", err);
          setIsAdaptivePolling(false);
          setAdaptiveTaskId(null);
          localStorage.removeItem('adaptive_task_id');
          clearInterval(pollInterval);
          navigate('/trains');
        } else {
          console.error("Ошибка при проверке статуса адаптации:", err);
          // Для других ошибок продолжаем попытки
        }
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [isAdaptivePolling, adaptiveTaskId, initData, isAuthenticated, getAdaptiveStatus, getTraings, navigate]);

  // Поллинг тренировок только если их нет
  useEffect(() => {
    const hasAuth = initData || localStorage.getItem("access_token");
    if (!hasAuth || !isReady || isAdaptivePolling) return;

    // На вкладке "Мои тренировки" не запускаем фоновый поллинг при отсутствии тренировок.
    if (activeTab !== "ai") return;

    // Запускаем поллинг только если статус SELECTED и тренировок нет
    if (currentUser?.select_samples !== UserStatus.SELECTED) return;
    if (trainings && trainings.length > 0) return; // Если тренировки есть, не запускаем поллинг

    const startTime = Date.now();

    const pollInterval = setInterval(async () => {
      if (document.hidden) {
        return;
      }

      const elapsed = Date.now() - startTime;
      
      // Если прошло более 3 минут, редиректим на /equipments
      if (elapsed >= POLLING_TIMEOUT) {
        clearInterval(pollInterval);
        navigate("/equipments");
        return;
      }

      try {
        const trainResult = await createTraining().unwrap();
        
        // Новая структура: trainings это объект с created_by_user и schedule
        const trainingsData = trainResult.trainings || {};
        const loadedUserTrainings = Array.isArray(trainingsData.created_by_user) 
          ? trainingsData.created_by_user 
          : [];
        const loadedScheduleTrainings = Array.isArray(trainingsData.schedule) 
          ? trainingsData.schedule 
          : [];
        const allTrainings = [...loadedUserTrainings, ...loadedScheduleTrainings];
        const count = trainResult.training_count || 0;
        
        // Если тренировки появились, останавливаем поллинг
        if (allTrainings.length > 0) {
          setUserTrainings(loadedUserTrainings);
          setScheduleTrainings(loadedScheduleTrainings);
          setTrainings(allTrainings);
          setTrainingCount(count);
          
          // Вычисляем доступные тренировки для пользователей без подписки
          const accessibleIds = (() => {
            if (!loadedScheduleTrainings || loadedScheduleTrainings.length === 0) {
              return [];
            }

            // Если есть подписка, все тренировки доступны
            if (hasActiveSubscription) {
              return loadedScheduleTrainings.map((trainItem: Training) => trainItem.id);
            }

            // Если нет подписки, доступны только первые training_count актуальных тренировок
            // НО должно быть открыто НЕ МЕНЕЕ 3 тренировок
            // Фильтруем только актуальные тренировки (status === 'wait' || null)
            const currentTrainings = loadedScheduleTrainings.filter(
              (train: Training) => train.status === 'wait' || train.status === null || train.status === undefined
            );

            // Сортируем актуальные тренировки по дате
            const sortedTrainings = [...currentTrainings].sort((a, b) => {
              const firstDate = new Date(a.schedule).getTime();
              const secondDate = new Date(b.schedule).getTime();
              return firstDate - secondDate;
            });

            // Используем максимум из training_count и 3 (минимум 3 тренировки)
            const allowedCount = Math.max(3, count);
            const allowedTrainings = sortedTrainings.slice(0, allowedCount);

            return allowedTrainings.map((trainItem: Training) => trainItem.id);
          })();

          setAllowedTrainingIds(accessibleIds);
          
          if (trainResult.interpretation) {
            setInterpretation(trainResult.interpretation);
          }
          if (Array.isArray(trainResult.stages)) {
            setStages(trainResult.stages);
          }
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.error("Error polling trainings:", err);
        // Не останавливаем поллинг при ошибке, продолжаем попытки
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [initData, isReady, currentUser?.select_samples, createTraining, isAdaptivePolling, trainings, navigate, activeTab, hasActiveSubscription]);


  const openResume = () => {
    window.open(TRAINING_SAMPLE_LINK, "_self");
    window.Telegram?.WebApp?.close?.();
  };

  const chooseTemplate = async (sampleId: number) => {
    const hasAuth = initData || localStorage.getItem("access_token");
    if (!hasAuth) return;

    try {
      const result = await createTemplate({
        sample_id: sampleId,
      }).unwrap();

      if (!result.error) {
        toast.success("Выберите оборудование");
        navigate(`/equipments?adaptiveMode=true`);
      } else {
        toast.error("Произошла ошибка");
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Произошла ошибка");
    }
  };

  const handleDeleteTraining = async (trainingId: number) => {
    const hasAuth = initData || localStorage.getItem("access_token");
    if (!hasAuth) return;

    try {
      await deleteTraining({
        training_id: trainingId,
      }).unwrap();
      toast.success("Тренировка удалена");
      // Обновляем список тренировок
      getTraings();
    } catch (error: any) {
      console.error("Ошибка удаления тренировки:", error);
      toast.error(error?.data?.detail || "Ошибка при удалении тренировки");
    }
  };

  const handleGetPlanClick = () => {
    tg.HapticFeedback.impactOccurred("medium");
    
    // Если нет подписки - редирект на страницу тарифов
    if (!hasActiveSubscription) {
      navigate("/subscription");
      return;
    }
    
    // Если есть подписка - находим ближайшую доступную тренировку и переходим на неё
    if (scheduleTrainings.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Находим ближайшую доступную тренировку (с учетом блокировки)
      const upcomingTraining = scheduleTrainings
        .filter((train) => {
          const trainDate = new Date(train.schedule);
          trainDate.setHours(0, 0, 0, 0);
          const isAvailable = hasActiveSubscription || allowedTrainingIds.includes(train.id);
          return trainDate >= today && (train.status === 'wait' || !train.status) && isAvailable;
        })
        .sort((a, b) => new Date(a.schedule).getTime() - new Date(b.schedule).getTime())[0];
      
      if (upcomingTraining) {
        navigate(`/workout-preview/${upcomingTraining.id}`);
        return;
      }
      
      // Если нет будущих доступных тренировок, берем ближайшую прошедшую доступную
      const pastTraining = scheduleTrainings
        .filter((train) => {
          const isAvailable = hasActiveSubscription || allowedTrainingIds.includes(train.id);
          return (train.status === 'wait' || !train.status) && isAvailable;
        })
        .sort((a, b) => new Date(b.schedule).getTime() - new Date(a.schedule).getTime())[0];
      
      if (pastTraining) {
        navigate(`/workout-preview/${pastTraining.id}`);
        return;
      }
    }
    
    // Если тренировок нет, показываем сообщение
    toast.error("Тренировки пока не доступны");
  };

  const handleCreateTrainingClick = () => {
    tg.HapticFeedback.impactOccurred("medium");
    setShowCreateTrainingModal(true);
  };

  const handleAdaptiveSubmit = async () => {
    const hasAuth = initData || localStorage.getItem("access_token");
    if (!hasAuth) return;
    tg.HapticFeedback.impactOccurred("medium");

    try {
      const result = await refreshSamples({
        userMsg: adaptiveUserMsg,
      }).unwrap();

      setAdaptiveUserMsg("");
      setShowAdaptiveTextarea(false);
      
      if (result.task_id) {
        // Сохраняем task_id в localStorage
        localStorage.setItem('adaptive_task_id', result.task_id);
        // Запускаем поллинг
        setAdaptiveTaskId(result.task_id);
        setIsAdaptivePolling(true);
        toast.success("Программа обновляется");
      } else {
        toast.error("Не удалось получить task_id");
      }
    } catch (error: any) {
      if (isQuizNotCompletedError(error)) {
        notifyQuizNotCompleted();
        navigate("/quiz");
        return;
      }
      console.error("Ошибка при запуске адаптации:", error);
      toast.error("Произошла ошибка. Попробуйте через пару минут.");
    }
  };

  const handleAdaptiveCancel = () => {
    tg.HapticFeedback.impactOccurred("medium");
    setShowAdaptiveTextarea(false);
    setAdaptiveUserMsg("");
  };

  // Генерация календаря на неделю
  const weekCalendarDays = useMemo(() => {
    const today = new Date();
    type CalendarDay = {
      dateStr: string; // Используем строку вместо Date
      dayName: string;
      formattedDate: string;
    };
    const days: CalendarDay[] = [];
    const dayNames = ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС"];
    
    // Находим понедельник текущей недели
    const monday = new Date(today);
    const dayOfWeek = monday.getDay();
    const diff = monday.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // adjust when day is sunday
    const mondayDate = new Date(monday.getFullYear(), monday.getMonth(), diff, 0, 0, 0, 0);
    
    // Применяем смещение недель
    const offset = activeTab === "ai" ? weekOffset : userWeekOffset;
    mondayDate.setDate(mondayDate.getDate() + offset * 7);
    
    // Создаем массив дней недели (ПН, ВТ, СР, ЧТ, ПТ, СБ, ВС)
    // Понедельник = 0, Вторник = 1, Среда = 2, Четверг = 3, Пятница = 4, Суббота = 5, Воскресенье = 6
    const dayOffsets = [0, 1, 2, 3, 4, 5, 6];
    
    dayOffsets.forEach((dayOffset, index) => {
      const date = new Date(mondayDate);
      date.setDate(mondayDate.getDate() + dayOffset);
      const dateStr = format(date, "yyyy-MM-dd");
      days.push({
        dateStr,
        dayName: dayNames[index],
        formattedDate: format(date, "dd.MM"),
      });
    });
    
    return days;
  }, [weekOffset, userWeekOffset, activeTab]);

  // Устанавливаем выбранную дату для пользовательских тренировок
  useEffect(() => {
    if (userTrainings.length > 0 && activeTab === "user") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Находим понедельник текущей недели
      const currentMonday = new Date(today);
      const dayOfWeek = currentMonday.getDay();
      const diff = currentMonday.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      currentMonday.setDate(diff);
      currentMonday.setHours(0, 0, 0, 0);
      
      // Находим ближайшую пользовательскую тренировку
      const upcomingTraining = userTrainings
        .filter((train) => {
          const trainDate = new Date(train.schedule);
          trainDate.setHours(0, 0, 0, 0);
          return trainDate >= today;
        })
        .sort((a, b) => new Date(a.schedule).getTime() - new Date(b.schedule).getTime())[0];
      
      if (upcomingTraining) {
        const trainingDate = new Date(upcomingTraining.schedule);
        trainingDate.setHours(0, 0, 0, 0);
        
        // Находим понедельник недели, где находится тренировка
        const trainingMonday = new Date(trainingDate);
        const trainingDayOfWeek = trainingMonday.getDay();
        const trainingDiff = trainingMonday.getDate() - trainingDayOfWeek + (trainingDayOfWeek === 0 ? -6 : 1);
        trainingMonday.setDate(trainingDiff);
        trainingMonday.setHours(0, 0, 0, 0);
        
        // Вычисляем разницу в неделях
        const weeksDiff = Math.floor((trainingMonday.getTime() - currentMonday.getTime()) / (1000 * 60 * 60 * 24 * 7));
        
        // Устанавливаем смещение недели и дату тренировки
        setUserWeekOffset(weeksDiff);
        setSelectedUserDate(trainingDate);
      } else {
        // Если нет будущих тренировок, берем ближайшую прошедшую или сегодня
        const allTrainings = [...userTrainings]
          .sort((a, b) => new Date(b.schedule).getTime() - new Date(a.schedule).getTime())[0];
        
        if (allTrainings) {
          const trainingDate = new Date(allTrainings.schedule);
          trainingDate.setHours(0, 0, 0, 0);
          
          // Находим понедельник недели, где находится тренировка
          const trainingMonday = new Date(trainingDate);
          const trainingDayOfWeek = trainingMonday.getDay();
          const trainingDiff = trainingMonday.getDate() - trainingDayOfWeek + (trainingDayOfWeek === 0 ? -6 : 1);
          trainingMonday.setDate(trainingDiff);
          trainingMonday.setHours(0, 0, 0, 0);
          
          // Вычисляем разницу в неделях
          const weeksDiff = Math.floor((trainingMonday.getTime() - currentMonday.getTime()) / (1000 * 60 * 60 * 24 * 7));
          
          // Устанавливаем смещение недели и дату тренировки
          setUserWeekOffset(weeksDiff);
          setSelectedUserDate(trainingDate);
        } else {
          setUserWeekOffset(0);
          setSelectedUserDate(today);
        }
      }
    } else if (activeTab === "user" && !selectedUserDate) {
      setUserWeekOffset(0);
      setSelectedUserDate(new Date());
    }
  }, [userTrainings, activeTab]);

  // Устанавливаем выбранную дату по умолчанию (ближайшая тренировка или текущая дата)
  useEffect(() => {
    if (scheduleTrainings.length > 0 && activeTab === "ai") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Находим понедельник текущей недели
      const currentMonday = new Date(today);
      const dayOfWeek = currentMonday.getDay();
      const diff = currentMonday.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      currentMonday.setDate(diff);
      currentMonday.setHours(0, 0, 0, 0);
      
      // Находим ближайшую тренировку
      const upcomingTraining = scheduleTrainings
        .filter((train) => {
          const trainDate = new Date(train.schedule);
          trainDate.setHours(0, 0, 0, 0);
          return trainDate >= today && (train.status === 'wait' || !train.status);
        })
        .sort((a, b) => new Date(a.schedule).getTime() - new Date(b.schedule).getTime())[0];
      
      if (upcomingTraining) {
        const trainingDate = new Date(upcomingTraining.schedule);
        trainingDate.setHours(0, 0, 0, 0);
        
        // Находим понедельник недели, где находится тренировка
        const trainingMonday = new Date(trainingDate);
        const trainingDayOfWeek = trainingMonday.getDay();
        const trainingDiff = trainingMonday.getDate() - trainingDayOfWeek + (trainingDayOfWeek === 0 ? -6 : 1);
        trainingMonday.setDate(trainingDiff);
        trainingMonday.setHours(0, 0, 0, 0);
        
        // Вычисляем разницу в неделях
        const weeksDiff = Math.floor((trainingMonday.getTime() - currentMonday.getTime()) / (1000 * 60 * 60 * 24 * 7));
        
        // Устанавливаем смещение недели и дату тренировки
        setWeekOffset(weeksDiff);
        setSelectedDate(trainingDate);
      } else {
        // Если нет будущих тренировок, берем ближайшую прошедшую или сегодня
        const allTrainings = scheduleTrainings
          .filter((train) => train.status === 'wait' || !train.status)
          .sort((a, b) => new Date(b.schedule).getTime() - new Date(a.schedule).getTime())[0];
        
        if (allTrainings) {
          const trainingDate = new Date(allTrainings.schedule);
          trainingDate.setHours(0, 0, 0, 0);
          
          // Находим понедельник недели, где находится тренировка
          const trainingMonday = new Date(trainingDate);
          const trainingDayOfWeek = trainingMonday.getDay();
          const trainingDiff = trainingMonday.getDate() - trainingDayOfWeek + (trainingDayOfWeek === 0 ? -6 : 1);
          trainingMonday.setDate(trainingDiff);
          trainingMonday.setHours(0, 0, 0, 0);
          
          // Вычисляем разницу в неделях
          const weeksDiff = Math.floor((trainingMonday.getTime() - currentMonday.getTime()) / (1000 * 60 * 60 * 24 * 7));
          
          // Устанавливаем смещение недели и дату тренировки
          setWeekOffset(weeksDiff);
          setSelectedDate(trainingDate);
        } else {
          setWeekOffset(0);
          setSelectedDate(today);
        }
      }
    } else if (activeTab === "ai" && !selectedDate) {
      setWeekOffset(0);
      setSelectedDate(new Date());
    }
  }, [scheduleTrainings, activeTab]);

  // Получаем все тренировки для выбранной даты (ИИ-тренер)
  const selectedTrainings = useMemo(() => {
    if (!selectedDate || scheduleTrainings.length === 0) return [];

    const selectedDateStr = format(selectedDate, "yyyy-MM-dd");
    return scheduleTrainings
      .filter((train) => {
        const trainDateStr = format(new Date(train.schedule), "yyyy-MM-dd");
        return trainDateStr === selectedDateStr && (train.status === "wait" || !train.status);
      })
      .sort((a, b) => {
        const aTime = new Date(a.schedule).getTime();
        const bTime = new Date(b.schedule).getTime();
        return aTime - bTime;
      });
  }, [selectedDate, scheduleTrainings]);

  // Проверяем, есть ли тренировка на конкретную дату
  const hasTrainingOnDate = useCallback((date: Date) => {
    if (scheduleTrainings.length === 0) return false;
    const dateCopy = new Date(date);
    dateCopy.setHours(0, 0, 0, 0);
    const dateStr = format(dateCopy, "yyyy-MM-dd");
    return scheduleTrainings.some((train) => {
      const trainDate = new Date(train.schedule);
      trainDate.setHours(0, 0, 0, 0);
      const trainDateStr = format(trainDate, "yyyy-MM-dd");
      return trainDateStr === dateStr && (train.status === 'wait' || !train.status);
    });
  }, [scheduleTrainings]);

  // Проверяем, есть ли пользовательская тренировка на конкретную дату
  const hasUserTrainingOnDate = useCallback((date: Date) => {
    if (userTrainings.length === 0) return false;
    const dateCopy = new Date(date);
    dateCopy.setHours(0, 0, 0, 0);
    const dateStr = format(dateCopy, "yyyy-MM-dd");
    return userTrainings.some((train) => {
      const trainDate = new Date(train.schedule);
      trainDate.setHours(0, 0, 0, 0);
      const trainDateStr = format(trainDate, "yyyy-MM-dd");
      return trainDateStr === dateStr;
    });
  }, [userTrainings]);

  // Получаем все пользовательские тренировки для выбранной даты
  const selectedUserTrainings = useMemo(() => {
    if (!selectedUserDate || userTrainings.length === 0) return [];

    const selectedDateStr = format(selectedUserDate, "yyyy-MM-dd");
    return userTrainings
      .filter((train) => {
        const trainDateStr = format(new Date(train.schedule), "yyyy-MM-dd");
        return trainDateStr === selectedDateStr;
      })
      .sort((a, b) => {
        const aTime = new Date(a.schedule).getTime();
        const bTime = new Date(b.schedule).getTime();
        return aTime - bTime;
      });
  }, [selectedUserDate, userTrainings]);

  // Показываем PreloaderCat во время адаптации
  if (isAdaptivePolling) {
    return (
      <>
        <Menu active={0} />
        <PreloaderCat hasMenu />
      </>
    );
  }

  if (!isReady || isTrainLoading) {
    return (
      <>
        <Menu active={0} />
        <Preloader hasMenu />
      </>
    );
  }

  return (
    <div className={`container ${activeTab === "ai" && scheduleTrainings.length > 0 ? "!mb-[120px]" : "!mb-[100px]"}`}>
      <Menu active={0} />

      {/* Табы для переключения между своими тренировками и ИИ тренером */}
      {currentUser?.select_samples === "Selected" && trainings !== null && (
        <div className={style.tabs}>
          <button
            className={`${style.tab} ${activeTab === "ai" ? style.tabActive : ""}`}
            onClick={() => {
              setActiveTab("ai");
              setWeekOffset(0); // Сбрасываем смещение при переключении на вкладку ИИ тренер
            }}
          >
            ИИ тренер
          </button>
          <button
            className={`${style.tab} ${activeTab === "user" ? style.tabActive : ""}`}
            onClick={() => {
              setActiveTab("user");
              setUserWeekOffset(0); // Сбрасываем смещение при переключении на вкладку Мои тренировки
            }}
          >
            Мои тренировки
          </button>
        </div>
      )}

      {isShowTemplates && !trainings ? (
        currentUser?.select_samples === UserStatus.REQUIRED_ANCET ? (
          <div>
            <div
              className={style.weekCalendar}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <img src={weekCalendar} alt="week-calendar-icon" />
                <h3>{NO_PROGRESS_MESSAGE}</h3>
              </div>
              <div>
                <button
                  className={style.weekCalendar__btn}
                  onClick={openResume}
                >
                  {GET_PROGRAM_BUTTON_TEXT}
                </button>
              </div>
            </div>

          </div>
        ) : (
          <div>
            <h2 className="trains__title" style={{ color: "#362f41" }}>
              {currentUser?.select_samples?.length} рекомендованные программы
            </h2>
            <Slider
              chooseTemplate={chooseTemplate}
              templates={currentUser?.select_samples}
              isTemplateLoading={isTemplateLoading}
              slidesTotal={currentUser?.select_samples?.length || 1}
            />
          </div>
        )
      ) : (
        trainings && (
          <>
            {activeTab === "user" && (
              <>
                {/* Календарь */}
                {userTrainings.length > 0 && (
                  <div className={style.calendarWrapper}>
                    <button
                      className={style.calendarNavButton}
                      onClick={() => {
                        setUserCalendarDirection("backward");
                        setUserWeekOffset(userWeekOffset - 1);
                      }}
                      aria-label="Предыдущая неделя"
                    >
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    <div 
                      key={`user-calendar-${userWeekOffset}`}
                      className={`${style.calendar} ${userCalendarDirection === "forward" ? style.calendarSlideEnter : style.calendarSlideEnterReverse}`}
                    >
                      {weekCalendarDays.map((day, index) => {
                        const selectedDateStr = selectedUserDate ? format(selectedUserDate, "yyyy-MM-dd") : null;
                        const isActive = selectedDateStr === day.dateStr;
                        const dayDate = new Date(day.dateStr + "T00:00:00");
                        const hasTraining = hasUserTrainingOnDate(dayDate);
                        return (
                          <div
                            key={index}
                            className={`${style.calendarDay} ${isActive ? style.calendarDayActive : ""} ${hasTraining ? style.calendarDayWithTraining : ""}`}
                            onClick={() => {
                              const newDate = new Date(day.dateStr + "T00:00:00");
                              setSelectedUserDate(newDate);
                            }}
                          >
                            {hasTraining ? (
                              <div className={style.calendarDayDot}></div>
                            ) : (
                              <div className={style.calendarDayDotEmpty}></div>
                            )}
                            <span className={style.calendarDayName}>{day.dayName}</span>
                            <span className={style.calendarDayDate}>{day.formattedDate}</span>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      className={style.calendarNavButton}
                      onClick={() => {
                        setUserCalendarDirection("forward");
                        setUserWeekOffset(userWeekOffset + 1);
                      }}
                      aria-label="Следующая неделя"
                    >
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                  </div>
                )}

                {/* Отображение тренировки */}
                {selectedUserTrainings.length > 0 ? (
                  selectedUserTrainings.map((training) => (
                    <div key={training.id} className={style.userTrainingCardWrap}>
                      <div
                        className={style.trainingCard}
                        onClick={() => navigate(`/workout-preview/${training.id}`)}
                        style={{ cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/workout-preview/${training.id}`);
                          }
                        }}
                      >
                        {training.title_photo && training.title_photo !== "none" ? (
                          <img
                            src={training.title_photo}
                            alt={training.title}
                            className={style.trainingImage}
                          />
                        ) : (
                          <div className={style.trainingImage} style={{ backgroundColor: "#e0e0e0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ color: "#999", fontSize: "16px" }}>Нет изображения</span>
                          </div>
                        )}
                        <div className={style.trainingOverlay}>
                          <div className={style.trainingTitle}>
                            {training.title}
                          </div>
                        </div>
                      </div>

                      <div className={style.userTrainingActions}>
                        <button
                          type="button"
                          className={style.userTrainingEditButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/edit-training/${training.id}`);
                          }}
                        >
                          Редактировать
                        </button>
                        <button
                          type="button"
                          className={style.userTrainingDeleteButton}
                          onClick={async (e) => {
                            e.stopPropagation();
                            const isConfirmed = window.confirm("Удалить тренировку?");
                            if (!isConfirmed) return;
                            await handleDeleteTraining(training.id);
                          }}
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  ))
                ) : userTrainings.length > 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <p style={{ color: "#666", fontSize: "16px" }}>
                      На выбранную дату нет тренировок
                    </p>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <p style={{ color: "#666", fontSize: "16px" }}>
                      У вас пока нет созданных тренировок
                    </p>
                  </div>
                )}
              </>
            )}
            {activeTab === "ai" && (
              <>
                {/* Интерпретация */}
                {scheduleTrainings.length > 0 && interpretation && (
                  <div className={style.interpretation}>
                    <h2 className={style.interpretationTitle}>Создан для вас</h2>
                    <p className={style.interpretationText}>{interpretation}</p>
                  </div>
                )}

                {/* Блок информации о программе */}
                {scheduleTrainings.length > 0 && (
                  <ProgramInfoBlock
                    stages={stages}
                    accent={accent}
                    injuries={injuries}
                    trainingDuration={trainingDuration}
                    quantity={scheduleTrainings.length}
                  />
                )}

                {/* Календарь */}
                {scheduleTrainings.length > 0 && (
                  <div className={style.calendarWrapper}>
                    <button
                      className={style.calendarNavButton}
                      onClick={() => {
                        setCalendarDirection("backward");
                        setWeekOffset(weekOffset - 1);
                      }}
                      aria-label="Предыдущая неделя"
                    >
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    <div 
                      key={`calendar-${weekOffset}`}
                      className={`${style.calendar} ${calendarDirection === "forward" ? style.calendarSlideEnter : style.calendarSlideEnterReverse}`}
                    >
                      {weekCalendarDays.map((day, index) => {
                        const selectedDateStr = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;
                        const isActive = selectedDateStr === day.dateStr;
                        const dayDate = new Date(day.dateStr + "T00:00:00");
                        const hasTraining = hasTrainingOnDate(dayDate);
                        return (
                          <div
                            key={index}
                            className={`${style.calendarDay} ${isActive ? style.calendarDayActive : ""} ${hasTraining ? style.calendarDayWithTraining : ""}`}
                            onClick={() => {
                              const newDate = new Date(day.dateStr + "T00:00:00");
                              setSelectedDate(newDate);
                            }}
                          >
                            {hasTraining ? (
                              <div className={style.calendarDayDot}></div>
                            ) : (
                              <div className={style.calendarDayDotEmpty}></div>
                            )}
                            <span className={style.calendarDayName}>{day.dayName}</span>
                            <span className={style.calendarDayDate}>{day.formattedDate}</span>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      className={style.calendarNavButton}
                      onClick={() => {
                        setCalendarDirection("forward");
                        setWeekOffset(weekOffset + 1);
                      }}
                      aria-label="Следующая неделя"
                    >
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                  </div>
                )}

                {/* Отображение тренировки */}
                {selectedTrainings.length > 0 ? (
                  selectedTrainings.map((training) => (
                    <div
                      key={training.id}
                      className={`${style.trainingCard} ${!hasActiveSubscription && !allowedTrainingIds.includes(training.id) ? style.trainingCardLocked : ""}`}
                      onClick={() => {
                        // Проверяем, заблокирована ли тренировка
                        if (!hasActiveSubscription && !allowedTrainingIds.includes(training.id)) {
                          navigate("/subscription");
                          return;
                        }
                        navigate(`/workout-preview/${training.id}`);
                      }}
                      style={{ cursor: "pointer" }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          // Проверяем, заблокирована ли тренировка
                          if (!hasActiveSubscription && !allowedTrainingIds.includes(training.id)) {
                            navigate("/subscription");
                            return;
                          }
                          navigate(`/workout-preview/${training.id}`);
                        }
                      }}
                    >
                      {training.title_photo && training.title_photo !== "none" ? (
                        <>
                          <img
                            src={training.title_photo}
                            alt={training.title}
                            className={style.trainingImage}
                          />
                          {!hasActiveSubscription && !allowedTrainingIds.includes(training.id) && (
                            <div className={style.trainingLockOverlay}>
                              <div className={style.trainingLockIcon}>
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
                                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                  <path d="M7 11V8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8V11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                </svg>
                              </div>
                              <div className={style.trainingLockText}>Требуется подписка</div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={style.trainingImage} style={{ backgroundColor: "#e0e0e0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ color: "#999", fontSize: "16px" }}>Нет изображения</span>
                        </div>
                      )}
                      <div className={style.trainingOverlay}>
                        <div className={style.trainingTitle}>
                          {training.title}
                        </div>
                      </div>
                    </div>
                  ))
                ) : scheduleTrainings.length > 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <p style={{ color: "#666", fontSize: "16px" }}>
                      На выбранную дату нет тренировок
                    </p>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <p style={{ color: "#666", fontSize: "16px" }}>
                      У вас пока нет сгенерированных тренировок
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )
      )}

      {/* Фиксированная кнопка внизу экрана */}
      {(activeTab === "ai" || activeTab === "user") && !showCreateTrainingModal && (
        <div className={style.fixedButtonContainer}>
          <button
            className={style.getPlanButtonFixed}
            onClick={activeTab === "ai" ? handleGetPlanClick : handleCreateTrainingClick}
          >
            {activeTab === "ai" ? "Получить мой план" : "Создать тренировку"}
          </button>
        </div>
      )}

      <UserAgreementLink />

      {showCreateTrainingModal && (
        <CreateTrainingModal
          onClose={() => setShowCreateTrainingModal(false)}
          initData={initData}
          onSuccess={() => {
            // Обновляем список тренировок после создания
            getTraings();
          }}
        />
      )}

    </div>
  );
};

